import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import RejectionDialog from './RejectionDialog';
import ManualClassificationModal from './ManualClassificationModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ClassificationQueue() {
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [confidenceFilter, setConfidenceFilter] = useState('all');

  // Selection
  const [selected, setSelected] = useState([]);

  // Dialogs
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualTransaction, setManualTransaction] = useState(null);

  useEffect(() => {
    fetchClassifications();
  }, [page, rowsPerPage, statusFilter, confidenceFilter]);

  const fetchClassifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      let url = `${API_URL}/api/accounting/classification/pending?`;
      url += `status=${statusFilter}&`;
      url += `limit=${rowsPerPage}&`;
      url += `offset=${page * rowsPerPage}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        let data = response.data.data.classifications || [];

        // Apply confidence filter on client side
        if (confidenceFilter !== 'all') {
          data = data.filter(c => {
            const confidence = c.suggested_confidence_1 || 0;
            if (confidenceFilter === 'high') return confidence >= 80;
            if (confidenceFilter === 'medium') return confidence >= 50 && confidence < 80;
            if (confidenceFilter === 'low') return confidence < 50;
            return true;
          });
        }

        setClassifications(data);
        setTotalCount(response.data.data.total || data.length);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching classifications:', err);
      setError(err.response?.data?.error?.message || 'Failed to load classifications');
      setLoading(false);
    }
  };

  const handleApprove = async (classificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/classification/approve/${classificationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Refresh the list
        fetchClassifications();
        // Clear selection
        setSelected(selected.filter(id => id !== classificationId));
      }
    } catch (error) {
      console.error('Error approving classification:', error);
      alert(error.response?.data?.error?.message || 'Failed to approve classification');
    }
  };

  const handleRejectClick = (classification) => {
    setSelectedClassification(classification);
    setRejectionDialogOpen(true);
  };

  const handleRejectSubmit = async ({ classificationId, reason, manuallyClassify }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/classification/reject/${classificationId}`,
        { reason },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Close rejection dialog
        setRejectionDialogOpen(false);

        // If user wants to manually classify, open manual modal
        if (manuallyClassify) {
          setManualTransaction({
            id: selectedClassification.source_id,
            description: selectedClassification.transaction_description,
            amount: selectedClassification.transaction_amount,
            currency: selectedClassification.transaction_currency,
            date: selectedClassification.transaction_date
          });
          setManualModalOpen(true);
        }

        // Refresh list
        fetchClassifications();
      }
    } catch (error) {
      console.error('Error rejecting classification:', error);
      alert(error.response?.data?.error?.message || 'Failed to reject classification');
    }
  };

  const handleEditClick = (classification) => {
    setManualTransaction({
      id: classification.source_id,
      description: classification.transaction_description,
      amount: classification.transaction_amount,
      currency: classification.transaction_currency,
      date: classification.transaction_date
    });
    setSelectedClassification(classification);
    setManualModalOpen(true);
  };

  const handleManualSave = (data) => {
    // Refresh list after manual classification
    fetchClassifications();
    setManualModalOpen(false);
    setManualTransaction(null);
    setSelectedClassification(null);
  };

  const handleBulkApprove = async () => {
    for (const id of selected) {
      await handleApprove(id);
    }
    setSelected([]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = classifications.map(c => c.id);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 50) return 'warning';
    return 'error';
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && classifications.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header with filters */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight="600">
            Classification Queue
          </Typography>

          <Box display="flex" gap={2}>
            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            {/* Confidence Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Confidence</InputLabel>
              <Select
                value={confidenceFilter}
                onChange={(e) => {
                  setConfidenceFilter(e.target.value);
                  setPage(0);
                }}
                label="Confidence"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="high">High (≥80%)</MenuItem>
                <MenuItem value="medium">Medium (50-79%)</MenuItem>
                <MenuItem value="low">Low (&lt;50%)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Bulk Actions */}
        {selected.length > 0 && (
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Typography variant="body2">
              {selected.length} selected
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<ApproveIcon />}
              onClick={handleBulkApprove}
            >
              Bulk Approve ({selected.length})
            </Button>
          </Box>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < classifications.length}
                    checked={classifications.length > 0 && selected.length === classifications.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Suggested Account</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell align="center">Confidence</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classifications.map((classification) => {
                const isSelected = selected.indexOf(classification.id) !== -1;

                return (
                  <TableRow key={classification.id} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectOne(classification.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(classification.transaction_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {classification.transaction_description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        {formatCurrency(classification.transaction_amount, classification.transaction_currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {classification.suggested_account_code} - {classification.suggested_account_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {classification.suggested_brand_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={classification.suggested_reasoning_1 || 'No reasoning provided'}>
                        <Chip
                          label={`${classification.suggested_confidence_1 || 0}%`}
                          color={getConfidenceColor(classification.suggested_confidence_1 || 0)}
                          size="small"
                          icon={<InfoIcon />}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(classification.id)}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(classification)}
                          >
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(classification)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}

              {classifications.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      No classifications found matching your filters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Rejection Dialog */}
      <RejectionDialog
        open={rejectionDialogOpen}
        onClose={() => {
          setRejectionDialogOpen(false);
          setSelectedClassification(null);
        }}
        onReject={handleRejectSubmit}
        classification={selectedClassification}
      />

      {/* Manual Classification Modal */}
      <ManualClassificationModal
        open={manualModalOpen}
        onClose={() => {
          setManualModalOpen(false);
          setManualTransaction(null);
          setSelectedClassification(null);
        }}
        onSave={handleManualSave}
        transaction={manualTransaction}
        suggestedClassification={selectedClassification}
      />
    </Box>
  );
}
